import type {
  FavoriteProblemSearchRequest,
  GetMinimumProblemSchoolTypeGradeV2Data,
  ProblemMinimumSchoolTypeGradeRequest,
  ProblemRecommendationByConceptRequest,
  ProblemRecommendationByProblemRequest,
} from '~/api-spec/__generated__/main'
import { httpClient, type RequestMeta } from '~/http/client'

import { adaptProblemMinimumTrieKey } from './adapters'
import type { Problem, ProblemMinimumTrieKey } from './schemas'

// 추천·즐겨찾기 조회는 문제 본체가 아니라 교재 만들기의 문제 칸(CustomWorkbookProblemChildResponse)을
// 돌려줘 도메인 Problem 이 담지 못한다. 요청 함수는 그 모양을 가진 MakeCustomWorkbook 에 두고, 요청 타입만 여기서 발행한다.
//
// 스펙은 Kotlin 기본값이 있는 요청 필드를 required 로 그린다. 서버는 빠진 필드에 기본값을 채우므로
// (answerTypes=전체·practiceTestOption=INCLUDE·arithmeticOnly=false·maxProblemCount=50) 그 필드만 optional 로 푼다.
// by-problem 의 tagTop 은 서버가 `String?` 로 받지만 스펙이 nullable 을 빠뜨려 null 을 되살린다.
// 2026-09-30 백엔드 develop 의 problem/controller/request 세 Request.kt 로 확인.
type WithServerDefaults<Request, Defaulted extends keyof Request> = Omit<Request, Defaulted> &
  Partial<Pick<Request, Defaulted>>

type ProblemFilterDefaults = 'answerTypes' | 'practiceTestOption' | 'arithmeticOnly'

export type RecommendedProblemsByConceptParams = WithServerDefaults<
  ProblemRecommendationByConceptRequest,
  ProblemFilterDefaults | 'maxProblemCount'
>

// 기준 문제는 problemId·myDbProblemDetailId 중 정확히 하나로 가리킨다(둘 다 오거나 둘 다 없으면 400).
// 문제를 아직 발급받지 않은 나의DB 문항은 detailId 로 요청한다.
export type RecommendedProblemsByProblemParams = WithServerDefaults<
  Omit<ProblemRecommendationByProblemRequest, 'problemId' | 'myDbProblemDetailId' | 'tagTop'>,
  ProblemFilterDefaults
> & { tagTop?: ProblemRecommendationByProblemRequest['tagTop'] | null } & (
    | { problemId: Problem['id']; myDbProblemDetailId?: never }
    | {
        myDbProblemDetailId: NonNullable<
          ProblemRecommendationByProblemRequest['myDbProblemDetailId']
        >
        problemId?: never
      }
  )

export type FavoriteProblemsParams = WithServerDefaults<
  FavoriteProblemSearchRequest,
  ProblemFilterDefaults
>

export type ProblemIdsBody = ProblemMinimumSchoolTypeGradeRequest

/** 고른 문제들이 걸친 교육과정의 가장 앞선 학기. 응답은 CommonResponse 봉투 없이 본문 그대로다 */
export const getProblemMinimumTrieKey = async (
  body: ProblemIdsBody,
  meta?: RequestMeta,
): Promise<ProblemMinimumTrieKey> => {
  const data = await httpClient().post<GetMinimumProblemSchoolTypeGradeV2Data>(
    '/problems/minimum-trie-key',
    body,
    { meta },
  )
  return adaptProblemMinimumTrieKey(data)
}
